import type { ReactNode } from "react";
import { Link } from "@/i18n/routing";
import { categoriesBySlug } from "@/fixtures/ejinsight/article-fixtures";
import { EJINSIGHT_CATEGORY_TONE, EJINSIGHT_COLORS, EJINSIGHT_TYPE } from "@/lib/theme/ejinsight";

type PillTagProps = {
  children: ReactNode;
  category?: string;
  variant?: "soft" | "solid" | "outline";
  tone?: "primary" | "accent" | "ink";
  className?: string;
  /**
   * Render as a LINK to the category listing.
   *
   * Off by default, because most of these pills sit INSIDE a card's own
   * `<Link>` and an anchor inside an anchor is invalid markup — the browser
   * closes the outer one early and the card stops working. Opt in where the
   * pill stands on its own, which today is the article header.
   */
  linked?: boolean;
};

/**
 * A category label → its listing route.
 *
 * The labels are printed as they read ("HONG KONG"); the routes are slugs
 * (`hong-kong`). Anything not in the category set returns `undefined` and the
 * pill renders as a plain label rather than a link to a 404.
 */
function categoryHref(label?: string): string | undefined {
  if (!label) return undefined;
  const slug = label.trim().toLowerCase().replace(/\s+/g, "-");
  return slug in categoriesBySlug ? `/ejinsight/${slug}` : undefined;
}

const TONE_PRESETS = {
  primary: { fg: EJINSIGHT_COLORS.primary, bg: EJINSIGHT_COLORS.primarySoft },
  // accent tone uses the dark logo teal on a neutral teal-grey field
  accent: { fg: EJINSIGHT_COLORS.ink, bg: "#E1ECEC" },
  ink: { fg: EJINSIGHT_COLORS.ink, bg: "#F1F5F9" },
} as const;

export function PillTag({
  children,
  category,
  variant = "soft",
  tone = "primary",
  className,
  linked = false,
}: PillTagProps) {
  const key = category ? category.toUpperCase() : undefined;
  const categoryPalette = key ? EJINSIGHT_CATEGORY_TONE[key] : undefined;
  const palette = categoryPalette ?? TONE_PRESETS[tone];

  const styles =
    variant === "solid"
      ? { color: "#FFFFFF", backgroundColor: palette.fg }
      : variant === "outline"
        ? {
            color: palette.fg,
            backgroundColor: "transparent",
            border: `1px solid ${palette.fg}`,
          }
        : { color: palette.fg, backgroundColor: palette.bg };

  const href = linked ? categoryHref(category) : undefined;
  const classes = `inline-flex items-center rounded-full px-3 py-[5px] ${className ?? ""}`;

  // QA: "click on the category title tag should redirect to the category
  // listing page" — on the home page's cards and on the article header. The
  // pill was a `<span>` everywhere.
  if (href) {
    return (
      <Link
        href={href}
        className={`${classes} transition-opacity duration-150 hover:opacity-80 motion-reduce:transition-none`}
        style={{ ...EJINSIGHT_TYPE.pill, ...styles }}
      >
        {children}
      </Link>
    );
  }

  return (
    <span className={classes} style={{ ...EJINSIGHT_TYPE.pill, ...styles }}>
      {children}
    </span>
  );
}
